<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('crons::page.title.crons') }}</h2>

            <div>
                <b-datatable
                    :key="datatableKey"
                    :endpoint="listRoute"
                    :columns="columns"
                    :hidden-columns="hiddenColumns"
                    item-key="id"
                    has-filter-selector
                    :multi-sort="false"
                >
                    <template #filters="{ filters }">
                        <v-col cols="4" class="pb-0">
                            <b-filter-input
                                v-model="filters.filters.command"
                                :label="t('crons::grid.label.command')"
                            ></b-filter-input>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-select
                                v-model="filters.filters.is_active"
                                :items="filterItems"
                                item-text="label"
                                item-value="value"
                                :clearable="filters.filters.is_active !== null"
                                :label="t('crons::grid.label.is_active')"
                            >
                            </b-filter-select>
                        </v-col>
                    </template>
                    <template #[`item.allowed_for_guests`]="{ item }">
                        <div class="grid-status">
                            <span>
                                {{ item.allowed_for_guests ? t('default::label.yes') : t('default::label.no') }}
                            </span>
                        </div>
                    </template>
                    <template #[`item.allowed_for_all`]="{ item }">
                        <div class="grid-status">
                            <span>
                                {{ item.allowed_for_all ? t('default::label.yes') : t('default::label.no') }}
                            </span>
                        </div>
                    </template>
                    <template #[`item.is_active`]="{ item }">
                        <div class="grid-status">
                            <span>
                                {{ item.is_active ? t('default::status.active') : t('default::status.inactive') }}
                            </span>
                        </div>
                    </template>
                    <template #buttons>
                        <div class="px-2">
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        v-if="can('crons.create')"
                                        color="primary"
                                        icon
                                        v-bind="attrs"
                                        :href="getCreateUrl()"
                                        v-on="on"
                                    >
                                        <v-icon>mdi-plus-circle</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('crons::button.create.new_cron') }}</span>
                            </v-tooltip>
                        </div>
                    </template>
                    <template #[`item.actions`]="{ item }">
                        <div class="table-actions">
                            <v-tooltip v-if="can('api.v1.crons.update')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$root.goTo(editItemUrl(item))"
                                    >
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.edit') }}</span>
                            </v-tooltip>

                            <v-tooltip
                                v-if="can('api.v1.crons.switch-status') && item.is_active"
                                top
                                transition="v-fab-transition"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="switchStatus(item)"
                                    >
                                        <v-icon>mdi-toggle-switch-off</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.deactivate') }}</span>
                            </v-tooltip>

                            <v-tooltip
                                v-if="can('api.v1.crons.switch-status') && !item.is_active"
                                top
                                transition="v-fab-transition"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="switchStatus(item)"
                                    >
                                        <v-icon>mdi-toggle-switch</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.activate') }}</span>
                            </v-tooltip>

                            <b-delete
                                v-if="can('api.v1.crons.destroy')"
                                :title="t('default::confirmation.general')"
                                :text="t('default::confirmation.deletion')"
                                icon="mdi-trash-can"
                                :path="deleteItemUrl(item)"
                                @deleted="triggerDeleted"
                            >
                            </b-delete>
                        </div>
                    </template>
                </b-datatable>
            </div>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            hiddenColumns: ['id'],
            filterItems: [
                { label: t('default::label.all'), value: null },
                { label: t('default::label.active'), value: 1 },
                { label: t('default::label.inactive'), value: 0 },
            ],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('crons::grid.label.name'),
                    value: 'name',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('crons::grid.label.command'),
                    value: 'command',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('crons::grid.label.schedule'),
                    value: 'schedule',
                    width: 100,
                    sortable: false,
                },
                {
                    text: t('crons::grid.label.last_run_at'),
                    value: 'last_run_at',
                    width: 100,
                    sortable: false,
                },
                {
                    text: t('crons::grid.label.next_run_at'),
                    value: 'next_run_at',
                    width: 100,
                    sortable: false,
                },
                {
                    text: t('crons::grid.label.is_active'),
                    value: 'is_active',
                    width: 100,
                    sortable: false,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    align: 'end',
                    width: 140,
                },
            ],
        };
    },
    methods: {
        getCreateUrl() {
            return route('crons.create');
        },
        editItemUrl(item) {
            return route('crons.get', { cron: item.id });
        },
        switchStatus(item) {
            axios
                .put(route('api.v1.crons.switch-status', { cron: item.id }), '', {
                    headers: { 'Content-Type': 'text/plain' },
                })
                .then((r) => {
                    this.datatableKey++;
                    this.$root.notify('info', r.data.message);
                })
                .catch((e) => console.log(e));
        },
        deleteItemUrl(item) {
            return route('api.v1.crons.destroy', { cron: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
    },
};
</script>
